import { ApiError, NetworkError, AuthError } from './errors';
import { captureException } from './sentry';

/**
 * API Client Configuration
 */
interface ApiClientConfig {
  baseURL: string;
  headers?: Record<string, string>;
  timeout?: number;
  tokenGetter?: () => Promise<string | null>;
}

/**
 * Request options for API calls
 */
interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
  retries?: number;
  accessToken?: string | null;
  /**
   * When true (the default when a tokenGetter is configured), the request is
   * aborted before hitting the network if no token is available. Set to false
   * for genuinely public endpoints that require no Authorization header.
   */
  requiresAuth?: boolean;
}

/**
 * Type-safe HTTP client using native Fetch API
 * Features:
 * - Automatic error handling
 * - Retry logic with exponential backoff
 * - Authentication header injection
 * - Request/response interceptors
 * - Type-safe responses
 */
class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private defaultTimeout: number;
  private activeRequests: Map<string, AbortController>;
  private tokenGetter: (() => Promise<string | null>) | null;

  constructor(config: ApiClientConfig) {
    this.baseURL = config.baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.headers,
    };
    this.defaultTimeout = config.timeout || 30000; // 30 seconds
    this.activeRequests = new Map();
    this.tokenGetter = config.tokenGetter || null;
  }

  /**
   * Perform a GET request
   */
  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', url, undefined, options);
  }

  /**
   * Perform a POST request
   */
  async post<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', url, data, options);
  }

  /**
   * Perform a PATCH request
   */
  async patch<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PATCH', url, data, options);
  }

  /**
   * Perform a PUT request
   */
  async put<T>(url: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', url, data, options);
  }

  /**
   * Perform a DELETE request
   */
  async delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', url, undefined, options);
  }

  /**
   * Core request method with retry logic
   */
  private async request<T>(
    method: string,
    url: string,
    data?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    // Retry logic is handled by TanStack Query — the api-client does not retry
    // by default so the two layers don't multiply (e.g. 4 api × 4 TQ = 16 calls).
    // Pass retries > 0 only for callers that bypass TQ (e.g. fire-and-forget attribution).
    const maxRetries = options.retries ?? 0;
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await this.executeRequest<T>(method, url, data, options);
      } catch (error) {
        lastError = error as Error;

        // Don't retry on client errors (4xx), auth errors, or network errors
        // (NetworkError covers CORS failures and won't resolve on retry).
        if (error instanceof AuthError) {
          throw error;
        }
        if (error instanceof NetworkError) {
          throw error;
        }
        if (error instanceof ApiError && error.statusCode >= 400 && error.statusCode < 500) {
          throw error;
        }

        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          throw error;
        }

        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attempt) * 1000;
        await new Promise((resolve) => setTimeout(resolve, delay));

        if (import.meta.env.DEV) {
          console.log(
            `Retrying ${method} ${url} request (attempt ${attempt + 1}/${maxRetries})...`,
          );
        }
      }
    }

    throw lastError || new Error('Request failed');
  }

  /**
   * Execute a single request
   */
  private async executeRequest<T>(
    method: string,
    url: string,
    data?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const fullURL = `${this.baseURL}${url}`;
    const requestKey = `${method}:${fullURL}`;

    // Request deduplication for GET requests only — cancelling a previous in-flight
    // mutation (POST/PATCH/DELETE) would silently abort a write the caller expects to land.
    if (method === 'GET' && this.activeRequests.has(requestKey)) {
      this.activeRequests.get(requestKey)?.abort();
    }

    const abortController = new AbortController();
    this.activeRequests.set(requestKey, abortController);

    try {
      // Build headers
      const headers = new Headers({
        ...this.defaultHeaders,
        ...options.headers,
      });

      // Resolve the token. Priority: explicit options.accessToken > tokenGetter.
      let token: string | null = null;
      if (options.accessToken !== undefined) {
        token = options.accessToken;
      } else if (this.tokenGetter) {
        token = await this.tokenGetter();
      }

      // If auth is required (the default when a tokenGetter is wired up) and no
      // token is available, bail out now — don't make a network call that will
      // just be rejected with 401.
      const requiresAuth = options.requiresAuth ?? this.tokenGetter !== null;
      if (requiresAuth && !token) {
        throw new AuthError('No access token available');
      }

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      // Build request options
      const fetchOptions: RequestInit = {
        method,
        headers,
        signal: abortController.signal,
      };

      // Add body for non-GET requests
      if (data && method !== 'GET') {
        fetchOptions.body = JSON.stringify(data);
      }

      // Set timeout
      const timeoutId = setTimeout(() => {
        abortController.abort();
      }, options.timeout || this.defaultTimeout);

      // Make the request
      let response: Response;
      try {
        response = await fetch(fullURL, fetchOptions);
      } catch (error) {
        clearTimeout(timeoutId);

        if (error instanceof Error && error.name === 'AbortError') {
          const timeoutErr = new NetworkError('Request timeout');
          captureException(timeoutErr, { method, url: fullURL });
          throw timeoutErr;
        }

        const networkErr = new NetworkError('Network request failed');
        captureException(networkErr, { method, url: fullURL });
        throw networkErr;
      } finally {
        clearTimeout(timeoutId);
        this.activeRequests.delete(requestKey);
      }

      // Handle response
      return await this.handleResponse<T>(response);
    } catch (error) {
      this.activeRequests.delete(requestKey);
      throw error;
    }
  }

  /**
   * Handle HTTP response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    // Get response body
    let responseData: unknown;
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    // Handle errors
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        throw new AuthError(
          typeof responseData === 'object' && responseData && 'message' in responseData
            ? String(responseData.message)
            : 'Authentication required',
        );
      }

      const apiError = new ApiError(
        typeof responseData === 'object' && responseData && 'message' in responseData
          ? String(responseData.message)
          : `HTTP Error ${response.status}`,
        response.status,
        responseData,
      );
      // Report unexpected server errors (5xx) to Sentry; 4xx are client/user errors.
      if (response.status >= 500) {
        captureException(apiError, { url: response.url, status: response.status });
      }
      throw apiError;
    }

    return responseData as T;
  }

  /**
   * Cancel all active requests
   */
  cancelAll(): void {
    this.activeRequests.forEach((controller) => controller.abort());
    this.activeRequests.clear();
  }

  /**
   * Update base URL (useful for environment switching)
   */
  setBaseURL(baseURL: string): void {
    this.baseURL = baseURL;
  }
}

/**
 * Module-level reference to WorkOS getAccessToken function
 * Set by initializeApiAuth during app startup
 */
let workosGetAccessToken: (() => Promise<string | null>) | null = null;

/**
 * Token getter that uses the initialized WorkOS function
 */
async function getAccessToken(): Promise<string | null> {
  if (!workosGetAccessToken) {
    return null;
  }
  try {
    return await workosGetAccessToken();
  } catch (error) {
    console.error('Failed to get access token:', error);
    return null;
  }
}

/**
 * Initialize the token getter with WorkOS getAccessToken
 * Must be called once during app initialization from ApiAuthProvider
 */
export function initializeApiAuth(getAccessTokenFn: () => Promise<string | null>): void {
  workosGetAccessToken = getAccessTokenFn;
}

/**
 * Create and export the API client instance
 */
export const apiClient = new ApiClient({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003',
  tokenGetter: getAccessToken,
});

/**
 * Export the ApiClient class for testing purposes
 */
export { ApiClient };
